// src/store/customer/useProfileStore.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { toast } from "react-hot-toast";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import useAuthStore from "../useAuthStore";

const useProfileStore = create(
  persist((set, get) => ({ // Added get to use fetchProfile internally
    isLoading: false,
    isUpdating: false,
    profile: null,

    fetchProfile: async () => {
      set({ isLoading: true });
      const { token, user } = useAuthStore.getState();

      if (!token || !user || !user.customerId) {
        // toast.error("Customer authentication details are missing.");
        console.log("Customer authentication details are missing.");
        set({ isLoading: false, profile: null }); // Ensure profile is null on error
        return;
      }

      try {
        const customerId = user.customerId;
        const response = await axiosInstancev1.get(`user-info/customer/${customerId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        set({ profile: response.data.customer, isLoading: false });
      } catch (error) {
        set({ isLoading: false, profile: null }); // Ensure profile is null on error
        toast.error(error.response?.data?.message || "Failed to fetch profile");
      }
    },

    updateProfile: async (profileData) => {
      set({ isUpdating: true });
      const { token, user } = useAuthStore.getState();

      if (!token || !user || !user.customerId) {
        toast.error("Customer authentication details are missing for update.");
        set({ isUpdating: false });
        return false;
      }

      const customerId = user.customerId;
      let dataToSend;

      // Check if profileData is already FormData (e.g., from image upload)
      // or if it's an object that needs conversion.
      if (profileData instanceof FormData) {
        dataToSend = profileData;
      } else {
        // Convert plain object to FormData
        dataToSend = new FormData();
        for (const key in profileData) {
          if (profileData[key] !== null && profileData[key] !== undefined) {
            // Backend might expect nulls for empty optional fields, adjust if needed
            // For files (like profile_image), this path won't be taken if it's already FormData
            dataToSend.append(key, profileData[key]);
          }
        }
      }

      try {
        // The cURL example uses /profile, but we are using /${customerId}
        // Ensure your backend supports multipart/form-data for this endpoint
        const response = await axiosInstancev1.put(
          `user-info/customer/${customerId}`, // Sticking to existing endpoint pattern
          dataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              // Axios will set Content-Type to multipart/form-data automatically for FormData
            },
          }
        );

        if (response.data.success) {
          toast.success(response.data.message || "Profile updated successfully");
          await get().fetchProfile(); // Re-fetch profile to get updated data
          set({ isUpdating: false });
          return true; // Indicate success
        } else {
          // This case might not be hit if API throws error for non-success
          toast.error(response.data.message || "Failed to update profile.");
          set({ isUpdating: false });
          return false;
        }

      } catch (error) {
        set({ isUpdating: false });
        const errorMsg = error.response?.data?.message || "Failed to update profile";
        if (error.response?.data?.errors) {
          const validationErrors = Object.values(error.response.data.errors).join(', ');
          toast.error(`Validation failed: ${validationErrors}`);
        } else {
          toast.error(errorMsg);
        }
        return false; // Indicate failure
      }
    }
  }),
    {
      name: "customer-profile-storage",
      getStorage: () => localStorage,
      // Do not persist runtime profile data to avoid stale data across logins
      partialize: () => ({})
    }
  )
);

export default useProfileStore;